/**
 * API Client for Virtual Tour Easy Dashboard
 * Handles all HTTP requests with automatic token refresh
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787'

// Request timeout in milliseconds
const REQUEST_TIMEOUT = 30000

class ApiError extends Error {
  status: number
  data: unknown

  constructor(message: string, status: number, data?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

interface RequestOptions extends RequestInit {
  skipAuth?: boolean
  timeout?: number
}

// Token refresh state to prevent race conditions
let isRefreshing = false
let refreshPromise: Promise<boolean> | null = null

async function request<T = any>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { skipAuth = false, ...fetchOptions } = options

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>)
  }

  // Add auth token if available
  if (!skipAuth) {
    const token = localStorage.getItem('vte_access_token')
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  }

  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      credentials: 'include'
    })

    // Handle 401 - try to refresh token
    if (response.status === 401 && !skipAuth) {
      const refreshed = await tryRefreshToken()
      if (refreshed) {
        // Retry the request with new token
        const newToken = localStorage.getItem('vte_access_token')
        headers['Authorization'] = `Bearer ${newToken}`
        const retryResponse = await fetch(url, {
          ...fetchOptions,
          headers,
          credentials: 'include'
        })
        return handleResponse<T>(retryResponse)
      }
      // Refresh failed, redirect to login
      localStorage.removeItem('vte_access_token')
      localStorage.removeItem('vte_refresh_token')
      window.location.href = '/login'
      throw new ApiError('Session expired', 401)
    }

    return handleResponse<T>(response)
  } catch (error) {
    if (error instanceof ApiError) throw error
    throw new ApiError(
      error instanceof Error ? error.message : 'Network error',
      0
    )
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type')
  const isJson = contentType?.includes('application/json')

  if (!response.ok) {
    const data = isJson ? await response.json() : await response.text()
    throw new ApiError(
      data?.error || data?.message || `HTTP error ${response.status}`,
      response.status,
      data
    )
  }

  if (isJson) {
    return response.json()
  }

  return response.text() as unknown as T
}

async function tryRefreshToken(): Promise<boolean> {
  // If already refreshing, wait for that to complete (prevents race condition)
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  const refreshToken = localStorage.getItem('vte_refresh_token')
  if (!refreshToken) return false

  isRefreshing = true

  refreshPromise = (async () => {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT)

      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
        signal: controller.signal,
        credentials: 'include'
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        // Clear tokens on refresh failure
        localStorage.removeItem('vte_access_token')
        localStorage.removeItem('vte_refresh_token')
        return false
      }

      const data = await response.json()
      localStorage.setItem('vte_access_token', data.accessToken)
      localStorage.setItem('vte_refresh_token', data.refreshToken)
      return true
    } catch {
      return false
    } finally {
      isRefreshing = false
      refreshPromise = null
    }
  })()

  return refreshPromise
}

// API methods
const api = {
  get: <T = any>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  post: <T = any>(endpoint: string, body?: any, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined
    }),

  put: <T = any>(endpoint: string, body?: any, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined
    }),

  patch: <T = any>(endpoint: string, body?: any, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined
    }),

  delete: <T = any>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),

  /** Fetch AI credit balance, cost reference, plan limits, and recent usage */
  getAiUsage: <T = any>() =>
    request<T>('/api/ai/credits', { method: 'GET' }),

  /** Request a short-lived preview token for an unpublished tour (owner only, 1-hour TTL) */
  requestPreviewToken: (tourUuid: string) =>
    request<{ preview_token: string }>(`/api/tours/${tourUuid}/preview-token`, { method: 'POST' })
}

export default api
export { ApiError }
